import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Boxes, Loader2, Search, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { StockStatusPill } from "@/components/StockStatus";
import PrefTable from "@/components/PrefTable";

export default function StockOverviewPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [lowOnly, setLowOnly] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/stock/overview", { params: { q: q || undefined, page, low_stock: lowOnly || undefined } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, page, lowOnly]);

  const STOCK_COLUMNS = [
    { key: "title", label: "Product", mandatory: true, defaultWidth: 300,
      render: (v, p) => <div><div className="font-medium text-slate-800 truncate max-w-[280px]">{v}</div><div className="text-xs text-slate-400 font-mono">{p.legacy_id}{p.sku ? ` · ${p.sku}` : ""}</div></div> },
    { key: "category", label: "Category", render: (v) => v || "—" },
    { key: "total_stock", label: "Held", align: "right", render: (v) => <span className="tabular-nums">{v}</span> },
    { key: "available", label: "Available", align: "right", render: (v) => <span className="tabular-nums font-semibold text-emerald-600">{v}</span> },
    { key: "allocated_total", label: "Allocated", align: "right", render: (v) => <span className="tabular-nums text-amber-600">{v || 0}</span> },
    { key: "on_order", label: "On Order", align: "right", render: (v) => <span className="tabular-nums text-slate-500">{v || 0}</span> },
    { key: "min_stock_level", label: "Min", align: "right", render: (v) => <span className="tabular-nums text-slate-400">{v ?? "—"}</span> },
    { key: "stock_status", label: "Status", render: (v) => <StockStatusPill status={v} /> },
  ];

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="stock-overview">
      <div className="flex items-center gap-3 mb-1"><PageIcon />
        <div><h1 className="text-2xl font-bold text-slate-900">Stock Overview</h1><p className="text-sm text-slate-500">Live totals calculated from per-location balances.</p></div>
      </div>
      <div className="flex items-center gap-3 my-5 flex-wrap">
        <div className="relative flex-1 min-w-[220px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search product, SKU, barcode…" className="pl-9" data-testid="stock-overview-search" /></div>
        <button onClick={() => { setPage(1); setLowOnly((v) => !v); }} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${lowOnly ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid="stock-low-toggle"><AlertTriangle className="w-4 h-4" /> Low stock only</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden p-3">
        {loading ? <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          : <PrefTable tableId="stock_overview" columns={STOCK_COLUMNS} rows={data.items} rowKey={(p) => p.legacy_id}
              onRowClick={(p) => navigate(`/products/${p.legacy_id}`)} rowTestId={(p) => `stock-row-${p.legacy_id}`} emptyText="No products found." testid="stock-overview-table-wrap" />}
      </div>
      <Pager page={data.page} totalPages={data.total_pages} total={data.total} onPage={setPage} />
    </div>
  );
}

export function Pager({ page, totalPages, total, onPage }) {
  return (
    <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
      <span>{total} records</span>
      <div className="flex items-center gap-2">
        <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50" data-testid="pager-prev">Prev</button>
        <span>Page {page} / {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => onPage(page + 1)} className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50" data-testid="pager-next">Next</button>
      </div>
    </div>
  );
}
